/**
 * Drive Sharing Service
 *
 * Handles Google Drive folder management for shared spaces.
 * Creates folders, manages permissions, and shares with space members.
 * Uses REST API instead of gapi for consistency with other services.
 */

import { getAccessToken } from '../lib/firebase';

export interface DriveFolder {
  id: string;
  name: string;
  webViewLink: string;
}

/**
 * Drive Sharing Client - REST API implementation
 */
class DriveSharingClient {
  private accessToken: string | null = null;
  private readonly API_BASE = 'https://www.googleapis.com/drive/v3';

  /**
   * Initialize with access token
   */
  initialize(accessToken: string): void {
    this.accessToken = accessToken;
  }

  /**
   * Ensure valid token
   */
  private async ensureValidToken(): Promise<string> {
    const freshToken = await getAccessToken();
    if (freshToken) {
      this.accessToken = freshToken;
      return freshToken;
    }

    if (this.accessToken) {
      return this.accessToken;
    }

    throw new Error('No valid access token available. Please sign in again.');
  }

  /**
   * Make authenticated request to Drive API
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = await this.ensureValidToken();
    const url = `${this.API_BASE}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Drive API error (${response.status}): ${errorText}`);
    }

    // Handle empty responses (e.g., DELETE returns 204 No Content)
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      return {} as T;
    }

    return response.json();
  }

  /**
   * Check if client is initialized
   */
  isInitialized(): boolean {
    return this.accessToken !== null;
  }

  // Drive API methods will be added below
  async createFolder(name: string, parentId?: string): Promise<DriveFolder> {
    const metadata: any = {
      name,
      mimeType: 'application/vnd.google-apps.folder',
    };

    if (parentId) {
      metadata.parents = [parentId];
    }

    const result = await this.request<any>('/files?fields=id,name,webViewLink', {
      method: 'POST',
      body: JSON.stringify(metadata),
    });

    return {
      id: result.id,
      name: result.name,
      webViewLink: result.webViewLink,
    };
  }

  async listFiles(query: string, fields: string): Promise<any> {
    const params = new URLSearchParams({
      q: query,
      fields,
    });

    return this.request<any>(`/files?${params.toString()}`);
  }

  async getFile(fileId: string, fields: string): Promise<any> {
    const params = new URLSearchParams({ fields });
    return this.request<any>(`/files/${fileId}?${params.toString()}`);
  }

  async updateFile(fileId: string, metadata: any): Promise<void> {
    await this.request(`/files/${fileId}`, {
      method: 'PATCH',
      body: JSON.stringify(metadata),
    });
  }

  async createPermission(
    fileId: string,
    permission: {
      type: string;
      role: string;
      emailAddress: string;
    },
    sendNotificationEmail: boolean = true,
    emailMessage?: string
  ): Promise<void> {
    const params = new URLSearchParams({
      sendNotificationEmail: sendNotificationEmail.toString(),
      fields: 'id',
    });

    if (emailMessage) {
      params.append('emailMessage', emailMessage);
    }

    await this.request(`/files/${fileId}/permissions?${params.toString()}`, {
      method: 'POST',
      body: JSON.stringify(permission),
    });
  }

  async listPermissions(fileId: string): Promise<any> {
    return this.request<any>(
      `/files/${fileId}/permissions?fields=permissions(id,emailAddress,role,type)`
    );
  }

  async deletePermission(fileId: string, permissionId: string): Promise<void> {
    await this.request(`/files/${fileId}/permissions/${permissionId}`, {
      method: 'DELETE',
    });
  }
}

// Create singleton instance
const driveSharingClient = new DriveSharingClient();

/**
 * Create a folder in Google Drive for a space
 * Creates: NIDOnote/{spaceName}/
 */
export async function createSpaceFolder(spaceName: string): Promise<DriveFolder> {
  // First, ensure NIDOnote root folder exists
  const rootFolderId = await ensureRootFolder();

  // Create space folder inside NIDOnote using REST API
  return driveSharingClient.createFolder(spaceName, rootFolderId);
}

/**
 * Ensure NIDOnote root folder exists, create if it doesn't
 */
async function ensureRootFolder(): Promise<string> {
  // Search for existing NIDOnote folder
  const response = await driveSharingClient.listFiles(
    "name='NIDOnote' and mimeType='application/vnd.google-apps.folder' and trashed=false",
    'files(id, name)'
  );

  if (response.files && response.files.length > 0) {
    return response.files[0].id;
  }

  // Create NIDOnote root folder
  const folder = await driveSharingClient.createFolder('NIDOnote');
  return folder.id;
}

/**
 * Share a Drive folder with a user via email
 * Grants edit permissions to space members
 */
export async function shareFolderWithUser(
  folderId: string,
  email: string,
  role: 'reader' | 'writer' | 'commenter' = 'writer'
): Promise<void> {
  try {
    await driveSharingClient.createPermission(
      folderId,
      {
        type: 'user',
        role: role,
        emailAddress: email,
      },
      true,
      `You've been added to a shared space in NIDOnote. You can now access and collaborate on notes and events in this space.`
    );
  } catch (error: any) {
    // If permission already exists, ignore error
    if (error.message?.includes('409')) {
      console.log('Permission already exists for', email);
      return;
    }
    throw error;
  }
}

/**
 * Remove user's access to a Drive folder
 */
export async function removeFolderAccess(
  folderId: string,
  email: string
): Promise<void> {
  try {
    // First, find the permission ID for this user
    const response = await driveSharingClient.listPermissions(folderId);

    const permission = response.permissions?.find(
      (p: any) => p.emailAddress?.toLowerCase() === email.toLowerCase()
    );

    if (permission && permission.id) {
      await driveSharingClient.deletePermission(folderId, permission.id);
    }
  } catch (error) {
    console.error('Failed to remove folder access:', error);
    throw error;
  }
}

/**
 * Update folder sharing for all space members
 * Call this when members are added/removed from a space
 */
export async function syncFolderPermissions(
  folderId: string,
  memberEmails: string[]
): Promise<void> {
  // Get current permissions
  const response = await driveSharingClient.listPermissions(folderId);

  const currentPermissions = response.permissions || [];
  const currentEmails = new Set(
    currentPermissions
      .filter((p: any) => p.emailAddress)
      .map((p: any) => p.emailAddress!.toLowerCase())
  );

  // Add new members
  const addPromises = memberEmails
    .filter((email: string) => !currentEmails.has(email.toLowerCase()))
    .map((email: string) => shareFolderWithUser(folderId, email));

  // Remove members who are no longer in the space
  const removePromises = currentPermissions
    .filter((p: any) => p.emailAddress && p.type === 'user')
    .filter((p: any) => !memberEmails.includes(p.emailAddress!.toLowerCase()))
    .map((p: any) => driveSharingClient.deletePermission(folderId, p.id!));

  await Promise.all([...addPromises, ...removePromises]);
}

/**
 * Get folder metadata
 */
export async function getFolderMetadata(folderId: string): Promise<DriveFolder> {
  const response = await driveSharingClient.getFile(folderId, 'id,name,webViewLink');

  return {
    id: response.id,
    name: response.name,
    webViewLink: response.webViewLink,
  };
}

/**
 * Check if folder exists and user has access
 */
export async function canAccessFolder(folderId: string): Promise<boolean> {
  try {
    await driveSharingClient.getFile(folderId, 'id');
    return true;
  } catch (error: any) {
    if (error.message?.includes('404') || error.message?.includes('403')) {
      return false;
    }
    throw error;
  }
}

/**
 * Create subfolder within a space folder
 * Used for organizing notes, events, etc.
 */
export async function createSubfolder(
  parentFolderId: string,
  folderName: string
): Promise<DriveFolder> {
  return driveSharingClient.createFolder(folderName, parentFolderId);
}

/**
 * List all shared folders (spaces) user has access to
 */
export async function listSharedSpaceFolders(): Promise<DriveFolder[]> {
  const rootFolderId = await ensureRootFolder();

  const response = await driveSharingClient.listFiles(
    `'${rootFolderId}' in parents and mimeType='application/vnd.google-apps.folder' and trashed=false`,
    'files(id, name, webViewLink)'
  );

  if (!response.files) {
    return [];
  }

  return response.files.map((file: any) => ({
    id: file.id,
    name: file.name,
    webViewLink: file.webViewLink,
  }));
}

/**
 * Delete a space folder (move to trash)
 * Only the owner should call this
 */
export async function deleteSpaceFolder(folderId: string): Promise<void> {
  await driveSharingClient.updateFile(folderId, {
    trashed: true,
  });
}

/**
 * Rename a space folder
 */
export async function renameSpaceFolder(
  folderId: string,
  newName: string
): Promise<void> {
  await driveSharingClient.updateFile(folderId, {
    name: newName,
  });
}

/**
 * Get Drive folder URL for opening in browser
 */
export function getDriveFolderUrl(folderId: string): string {
  return `https://drive.google.com/drive/folders/${folderId}`;
}

/**
 * Check if Drive API is loaded (now checks if client is initialized)
 */
export function isDriveApiLoaded(): boolean {
  return driveSharingClient.isInitialized();
}

/**
 * Initialize Drive Sharing Client with access token
 */
export function initializeDriveSharingClient(accessToken: string): void {
  driveSharingClient.initialize(accessToken);
}
